/**
 * Crisis Notification Service
 * Handles therapist notifications when patients express crisis indicators
 */

import { collection, addDoc, query, where, getDocs, Timestamp, updateDoc, doc, onSnapshot, Unsubscribe, orderBy, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { CrisisDetection } from '../types/convai';
import EmailNotificationService from './EmailNotificationService';

export interface CrisisAlert {
  id?: string;
  patientId: string;
  patientName: string;
  providerId: string;
  providerName: string;

  // Crisis details
  severity: 'low' | 'medium' | 'high' | 'critical' | 'urgency';
  keywords: string[];
  triggerMessage: string; // The patient's message that triggered the alert

  // Hope conversation context
  conversationTranscript?: { sender: string; text: string; time: string }[];
  alertType?: 'crisis' | 'urgency'; // urgency = non-crisis but concerning

  // Patient contact info (auto-populated)
  patientPhone?: string;
  patientEmergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
  };

  // Timestamps
  timestamp: Date;
  acknowledgedAt?: Date;
  acknowledgedBy?: string;

  // Status
  status: 'pending' | 'acknowledged' | 'resolved';

  // Actions taken
  actionsTaken?: string[];
  notes?: string;
}

class CrisisNotificationService {
  private static instance: CrisisNotificationService;
  private readonly COLLECTION_NAME = 'crisis_alerts';

  private constructor() {}

  public static getInstance(): CrisisNotificationService {
    if (!CrisisNotificationService.instance) {
      CrisisNotificationService.instance = new CrisisNotificationService();
    }
    return CrisisNotificationService.instance;
  }

  /**
   * Create a crisis alert and notify the patient's therapist
   */
  public async createCrisisAlert(
    patientId: string,
    patientName: string,
    providerId: string,
    providerName: string,
    crisisDetection: CrisisDetection,
    triggerMessage: string
  ): Promise<string> {
    try {
      const alert: Omit<CrisisAlert, 'id'> = {
        patientId,
        patientName,
        providerId,
        providerName,
        severity: crisisDetection.severity,
        keywords: crisisDetection.keywords,
        triggerMessage,
        timestamp: new Date(),
        status: 'pending',
      };

      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), {
        ...alert,
        timestamp: Timestamp.fromDate(alert.timestamp),
      });

      // Send email notification to provider
      this.sendCrisisEmail(providerId, patientName, crisisDetection, triggerMessage).catch(() => {
        // Best-effort — alert is already saved to Firestore
      });

      return docRef.id;
    } catch (error) {
      console.error('Error creating crisis alert:', error);
      throw new Error('Failed to create crisis alert');
    }
  }

  /**
   * Get all pending crisis alerts for a provider
   */
  public async getPendingAlerts(providerId: string): Promise<CrisisAlert[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('providerId', '==', providerId),
        where('status', '==', 'pending')
      );

      const querySnapshot = await getDocs(q);
      const alerts: CrisisAlert[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        alerts.push({
          id: doc.id,
          patientId: data.patientId,
          patientName: data.patientName,
          providerId: data.providerId,
          providerName: data.providerName,
          severity: data.severity,
          keywords: data.keywords,
          triggerMessage: data.triggerMessage,
          timestamp: data.timestamp.toDate(),
          acknowledgedAt: data.acknowledgedAt?.toDate(),
          acknowledgedBy: data.acknowledgedBy,
          status: data.status,
          actionsTaken: data.actionsTaken,
          notes: data.notes,
        });
      });

      // Sort by severity (critical first) and then by timestamp
      return alerts.sort((a, b) => {
        const severityOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, urgency: 4 };
        const severityDiff = (severityOrder[a.severity] ?? 5) - (severityOrder[b.severity] ?? 5);
        if (severityDiff !== 0) return severityDiff;
        return b.timestamp.getTime() - a.timestamp.getTime();
      });
    } catch (error) {
      console.error('Error fetching pending alerts:', error);
      return [];
    }
  }

  /**
   * Get all crisis alerts for a provider (including acknowledged and resolved)
   */
  public async getAllAlerts(providerId: string, limit: number = 50): Promise<CrisisAlert[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('providerId', '==', providerId)
      );

      const querySnapshot = await getDocs(q);
      const alerts: CrisisAlert[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        alerts.push({
          id: doc.id,
          patientId: data.patientId,
          patientName: data.patientName,
          providerId: data.providerId,
          providerName: data.providerName,
          severity: data.severity,
          keywords: data.keywords,
          triggerMessage: data.triggerMessage,
          timestamp: data.timestamp.toDate(),
          acknowledgedAt: data.acknowledgedAt?.toDate(),
          acknowledgedBy: data.acknowledgedBy,
          status: data.status,
          actionsTaken: data.actionsTaken,
          notes: data.notes,
        });
      });

      // Sort by timestamp (most recent first)
      return alerts.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()).slice(0, limit);
    } catch (error) {
      console.error('Error fetching all alerts:', error);
      return [];
    }
  }

  /**
   * Acknowledge a crisis alert
   */
  public async acknowledgeAlert(
    alertId: string,
    providerId: string,
    providerName: string
  ): Promise<void> {
    try {
      const alertRef = doc(db, this.COLLECTION_NAME, alertId);
      await updateDoc(alertRef, {
        status: 'acknowledged',
        acknowledgedAt: Timestamp.now(),
        acknowledgedBy: providerName,
      });

      console.log('Crisis alert acknowledged:', alertId);
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      throw new Error('Failed to acknowledge alert');
    }
  }

  /**
   * Resolve a crisis alert with notes and actions taken
   */
  public async resolveAlert(
    alertId: string,
    actionsTaken: string[],
    notes?: string
  ): Promise<void> {
    try {
      const alertRef = doc(db, this.COLLECTION_NAME, alertId);
      await updateDoc(alertRef, {
        status: 'resolved',
        actionsTaken,
        notes: notes || '',
      });

      console.log('Crisis alert resolved:', alertId);
    } catch (error) {
      console.error('Error resolving alert:', error);
      throw new Error('Failed to resolve alert');
    }
  }

  /**
   * Get crisis alert count for a provider (for badge display)
   */
  public async getPendingAlertCount(providerId: string): Promise<number> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('providerId', '==', providerId),
        where('status', '==', 'pending')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error) {
      console.error('Error fetching alert count:', error);
      return 0;
    }
  }

  /**
   * Subscribe to real-time pending crisis alerts for a provider.
   * Fires callback immediately and on every change.
   */
  public subscribeToPendingAlerts(
    providerId: string,
    callback: (alerts: CrisisAlert[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, this.COLLECTION_NAME),
      where('providerId', '==', providerId),
      where('status', '==', 'pending')
    );

    return onSnapshot(q, (snapshot) => {
      const alerts: CrisisAlert[] = snapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          patientId: data.patientId,
          patientName: data.patientName,
          providerId: data.providerId,
          providerName: data.providerName,
          severity: data.severity,
          keywords: data.keywords,
          triggerMessage: data.triggerMessage,
          alertType: data.alertType || 'crisis',
          conversationTranscript: data.conversationTranscript || [],
          patientPhone: data.patientPhone || '',
          patientEmergencyContact: data.patientEmergencyContact || undefined,
          timestamp: data.timestamp?.toDate() || new Date(),
          acknowledgedAt: data.acknowledgedAt?.toDate(),
          acknowledgedBy: data.acknowledgedBy,
          status: data.status,
          actionsTaken: data.actionsTaken,
          notes: data.notes,
        };
      });

      // Sort: critical first, urgency last, then by timestamp
      const severityOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, urgency: 4 };
      alerts.sort((a, b) => {
        const diff = (severityOrder[a.severity] ?? 3) - (severityOrder[b.severity] ?? 3);
        return diff !== 0 ? diff : b.timestamp.getTime() - a.timestamp.getTime();
      });

      callback(alerts);
    }, (error) => {
      console.error('Error in crisis alerts listener:', error);
      callback([]);
    });
  }

  /**
   * Subscribe to real-time pending alert count (for badge display).
   */
  public subscribeToPendingAlertCount(
    providerId: string,
    callback: (count: number) => void
  ): Unsubscribe {
    const q = query(
      collection(db, this.COLLECTION_NAME),
      where('providerId', '==', providerId),
      where('status', '==', 'pending')
    );

    return onSnapshot(q, (snapshot) => {
      callback(snapshot.size);
    }, (error) => {
      console.error('Error in crisis alert count listener:', error);
      callback(0);
    });
  }

  /**
   * Get patient's provider ID (therapist assigned to this patient)
   */
  public async getPatientProvider(patientId: string): Promise<{ providerId: string; providerName: string } | null> {
    try {
      // Query patients collection to find the patient's assigned provider
      const patientsQuery = query(
        collection(db, 'patients'),
        where('id', '==', patientId)
      );

      const patientsSnapshot = await getDocs(patientsQuery);

      if (patientsSnapshot.empty) {
        console.warn('Patient not found:', patientId);
        return null;
      }

      const patientData = patientsSnapshot.docs[0].data();
      const assignedProviderId = patientData.assignedProviderId;

      if (!assignedProviderId) {
        console.warn('No provider assigned to patient:', patientId);
        return null;
      }

      // Get provider details
      const providersQuery = query(
        collection(db, 'users'),
        where('uid', '==', assignedProviderId),
        where('role', '==', 'provider')
      );

      const providersSnapshot = await getDocs(providersQuery);

      if (providersSnapshot.empty) {
        console.warn('Assigned provider not found:', assignedProviderId);
        return null;
      }

      const providerData = providersSnapshot.docs[0].data();
      const providerName = `${providerData.firstName || ''} ${providerData.lastName || ''}`.trim() || 'Provider';

      return {
        providerId: assignedProviderId,
        providerName,
      };
    } catch (error) {
      console.error('Error fetching patient provider:', error);
      return null;
    }
  }

  /**
   * Send crisis alert email to the provider
   */
  private async sendCrisisEmail(
    providerId: string,
    patientName: string,
    crisisDetection: CrisisDetection,
    triggerMessage: string
  ): Promise<void> {
    try {
      // Look up provider email from Firestore users collection
      const userDoc = await getDoc(doc(db, 'users', providerId));
      const providerEmail = userDoc.data()?.email;
      const providerName = [userDoc.data()?.firstName, userDoc.data()?.lastName].filter(Boolean).join(' ') || 'Provider';

      if (!providerEmail) return;

      await EmailNotificationService.getInstance().sendCrisisAlertEmail({
        providerEmail,
        providerName,
        patientName,
        severity: crisisDetection.severity,
        keywords: crisisDetection.keywords,
        triggerMessage,
        timestamp: new Date(),
      });
    } catch {
      // Best-effort — crisis alert is already persisted in Firestore
    }
  }

  /**
   * Create a Hope conversation alert (crisis or urgency) with full transcript + contact info.
   * Auto-fetches patient phone and emergency contact from Firestore.
   */
  public async createHopeAlert(params: {
    patientId: string;
    patientName: string;
    providerId: string;
    providerName: string;
    alertType: 'crisis' | 'urgency';
    severity: CrisisAlert['severity'];
    keywords: string[];
    triggerMessage: string;
    conversationTranscript: { sender: string; text: string; time: string }[];
  }): Promise<string> {
    try {
      // Fetch patient contact info
      let patientPhone = '';
      let emergencyContact: CrisisAlert['patientEmergencyContact'];

      try {
        const patientDoc = await getDoc(doc(db, 'patients', params.patientId));
        if (patientDoc.exists()) {
          const pd = patientDoc.data();
          patientPhone = pd.phone || '';
          if (pd.emergencyContact) {
            emergencyContact = {
              name: pd.emergencyContact.name || '',
              phone: pd.emergencyContact.phone || '',
              relationship: pd.emergencyContact.relationship || '',
            };
          }
        }
      } catch { /* patient lookup is best-effort */ }

      const alertData = {
        patientId: params.patientId,
        patientName: params.patientName,
        providerId: params.providerId,
        providerName: params.providerName,
        severity: params.severity,
        keywords: params.keywords,
        triggerMessage: params.triggerMessage,
        alertType: params.alertType,
        conversationTranscript: params.conversationTranscript,
        patientPhone,
        patientEmergencyContact: emergencyContact || null,
        timestamp: Timestamp.fromDate(new Date()),
        status: 'pending',
      };

      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), alertData);

      // Send email for crisis (high/critical) alerts
      if (params.alertType === 'crisis') {
        this.sendCrisisEmail(params.providerId, params.patientName, {
          detected: true,
          severity: params.severity as any,
          keywords: params.keywords,
          recommendedAction: 'notify_provider',
        }, params.triggerMessage).catch(() => {});
      }

      return docRef.id;
    } catch (error) {
      console.error('Error creating Hope alert:', error);
      throw error;
    }
  }
}

export const crisisNotificationService = CrisisNotificationService.getInstance();
